import { useCallback } from 'react';
import { api, ApiError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatMoney } from '../lib/format';
import { ViewHeader } from './DashboardLayout';
import type { OverviewResponse } from './types';

/**
 * Dashboard → Overview (M10a).
 *
 * The sync-specific dashboard. Surfaces only state that neither HubSpot nor
 * Stripe exposes natively: sync-linkage status between the two systems, plus
 * the connection/health signals an admin needs to trust the integration.
 *
 * Deliberately does NOT reproduce HubSpot's reporting or Stripe's Billing
 * dashboard — those link out (the "Open in Stripe Billing" CTA).
 *
 * Reference: docs/README.md §M10-rescoped (Overview spec)
 *            src/handlers/dashboard.ts (the backend)
 */
export function Overview() {
  const fetcher = useCallback(
    (signal: AbortSignal) =>
      api.get<OverviewResponse>('admin/overview', { signal }),
    []
  );
  const { data, loading, error, refetch } = useAsync(fetcher);

  return (
    <>
      <ViewHeader
        title="Overview"
        subtitle="Sync health and linkage status between Stripe and HubSpot."
        actions={
          <button className="btn-ghost" onClick={refetch} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />

      {error && <ErrorCard error={error} onRetry={refetch} />}
      {!error && !data && loading && <LoadingSkeleton />}
      {data && <OverviewBody data={data} />}
    </>
  );
}

function OverviewBody({ data }: { data: OverviewResponse; }) {
  const { connection, stats } = data;

  return (
    <div className="space-y-6">
      {/* Bootstrap banner — only when a historical import is in flight or failed. */}
      {stats.bootstrap &&
        (stats.bootstrap.status === 'running' ||
          stats.bootstrap.status === 'paused' ||
          stats.bootstrap.status === 'failed') && (
          <BootstrapBanner bootstrap={stats.bootstrap} />
        )}

      {/* Connection card */}
      <div className="card p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-ink-500">
          Connection
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="HubSpot portal">
            <span className="font-mono text-sm text-ink-800">
              {connection.portalId ?? '—'}
            </span>
          </Field>
          <Field label="Stripe account">
            {connection.stripeConnected ? (
              <span className="font-mono text-sm text-ink-800">
                {connection.stripeAccountId}
              </span>
            ) : (
              <span className="badge-warn">Not connected</span>
            )}
          </Field>
          {/* OAuth token health is internal diagnostics — not surfaced to the
              end user (QA-F #1). The backend keeps `hubspotTokenExpiresAt` /
              `tokenHealthy` for the runbook / diagnostics. */}
          <Field label="Deal mirroring">
            {connection.createDeals ? (
              <span className="badge-success">Enabled</span>
            ) : (
              <span className="badge-neutral">Disabled</span>
            )}
          </Field>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <a
            href="https://dashboard.stripe.com/billing"
            target="_blank"
            rel="noreferrer"
            className="btn-secondary"
          >
            Open Stripe Billing ↗
          </a>
          <a
            href={`https://app.hubspot.com/portal/${connection.portalId ?? ''}`}
            target="_blank"
            rel="noreferrer"
            className="btn-secondary"
          >
            Open HubSpot ↗
          </a>
        </div>
      </div>

      {/* Sync-linkage status — the headline dashboard content */}
      <div className="card p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-ink-500">
          Sync-linkage status
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <SyncStateCard
            label="Subscriptions"
            counts={stats.subscriptions}
            to="/dashboard/subscriptions"
          />
          <SyncStateCard
            label="Customers"
            counts={stats.customers}
            to="/dashboard/customers"
          />
          <SyncStateCard
            label="Products"
            counts={stats.products}
            to="/dashboard/products"
          />
        </div>
        <p className="mt-4 text-xs text-ink-400">
          Synced = HubSpot object exists and is actively updated. Broken = the
          HubSpot target vanished (returned 404). Pending = staged for review.
          Ignored = admin opted out of propagation. The sidebar card always
          shows live Stripe state regardless of these flags.
        </p>
      </div>

      {/* Revenue + activity rollups */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active subscriptions" value={String(stats.activeSubscriptions)} />
        <StatCard
          label="Total active MRR"
          value={formatMoney(stats.totalMrrMinor, 'USD')}
          hint="Sum across active subscriptions"
        />
        <StatCard label="Invoices recorded" value={String(stats.totalInvoices)} />
        <StatCard
          label="Last sync activity"
          value={stats.lastActivityAt ? formatRelative(stats.lastActivityAt) : 'never'}
          hint={stats.lastActivityAt ? new Date(stats.lastActivityAt * 1000).toLocaleString() : undefined}
        />
      </div>

      {/* Health alerts */}
      {stats.brokenObjects > 0 && (
        <div className="card border-warn-500/40 bg-warn-500/5 p-6">
          <div className="flex items-start gap-3">
            <AlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-warn-600" />
            <div>
              <h2 className="text-sm font-semibold text-warn-600">
                {stats.brokenObjects} object{stats.brokenObjects === 1 ? '' : 's'} need attention
              </h2>
              <p className="mt-1 text-sm text-ink-600">
                Some synced objects were deleted in HubSpot and need to be
                relinked or ignored. Visit the Admin Staging Panel to review
                broken subscriptions, customers, and prices.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** One sync-state card with the four-state breakdown. */
function SyncStateCard({
  label,
  counts,
  to,
}: {
  label: string;
  counts: OverviewResponse['stats']['subscriptions'];
  to: string;
}) {
  return (
    <a
      href={to}
      className="block rounded-lg border border-ink-200 bg-ink-50/50 p-4 transition-colors hover:border-brand-300 hover:bg-brand-50/30"
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-ink-700">{label}</span>
        <span className="text-lg font-semibold text-ink-900">{counts.total}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Pill tone="success">✓ {counts.synced}</Pill>
        {counts.pending > 0 && <Pill tone="neutral">◐ {counts.pending} pending</Pill>}
        {counts.broken > 0 && <Pill tone="warn">⚠ {counts.broken} broken</Pill>}
        {counts.ignored > 0 && <Pill tone="neutral">⊘ {counts.ignored} ignored</Pill>}
        {counts.total === 0 && (
          <span className="text-xs text-ink-400">none yet</span>
        )}
      </div>
    </a>
  );
}

function Pill({ tone, children }: { tone: 'success' | 'warn' | 'neutral'; children: React.ReactNode; }) {
  const cls =
    tone === 'success'
      ? 'bg-success-500/10 text-success-600'
      : tone === 'warn'
        ? 'bg-warn-500/10 text-warn-600'
        : 'bg-ink-100 text-ink-500';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string; }) {
  return (
    <div className="card p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-ink-400">{label}</div>
      <div className="mt-1 text-xl font-semibold text-ink-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-ink-400">{hint}</div>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode; }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-ink-400">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function BootstrapBanner({ bootstrap }: { bootstrap: NonNullable<OverviewResponse['stats']['bootstrap']>; }) {
  const failed = bootstrap.status === 'failed';
  const running = bootstrap.status === 'running';
  const pct = running && bootstrap.totalProcessed > 0 ? '· in progress' : '';
  return (
    <div
      className={`card p-4 ${failed ? 'border-danger-500/40 bg-danger-500/5' : 'border-brand-300 bg-brand-50/40'}`}
    >
      <div className="flex items-start gap-3">
        {failed ? (
          <AlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-danger-600" />
        ) : (
          <SpinnerIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
        )}
        <div className="min-w-0 flex-1">
          <h2 className={`text-sm font-semibold ${failed ? 'text-danger-600' : 'text-brand-700'}`}>
            Historical import {bootstrap.status} — phase: {bootstrap.phase} {pct}
          </h2>
          <p className="mt-1 text-sm text-ink-600">
            {bootstrap.totalProcessed} processed
            {bootstrap.totalFailed > 0 && (
              <span className="text-warn-600"> · {bootstrap.totalFailed} failed</span>
            )}
            {failed && bootstrap.errorMessage && (
              <span className="block font-mono text-xs text-ink-500">
                {bootstrap.errorMessage}
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="card h-32 animate-pulse bg-ink-100/60" />
      <div className="card h-40 animate-pulse bg-ink-100/60" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="card h-20 animate-pulse bg-ink-100/60" />
        ))}
      </div>
    </div>
  );
}

function ErrorCard({ error, onRetry }: { error: Error; onRetry: () => void; }) {
  const detail = error instanceof ApiError ? ` (${error.status})` : '';
  return (
    <div className="card border-danger-500/40 bg-danger-500/5 p-6">
      <div className="flex items-start gap-3">
        <AlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-danger-600" />
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-danger-600">
            Couldn't load the overview{detail}
          </h2>
          <p className="mt-1 text-sm text-ink-600">{error.message}</p>
          <button className="btn-secondary mt-3" onClick={onRetry}>
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

/** Relative time: "3m ago", "2h ago", "just now". */
function formatRelative(unixSeconds: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - unixSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(unixSeconds * 1000).toLocaleDateString();
}

// ─── inline icons (no extra dep) ───────────────────────────────────────────

function AlertIcon({ className = '' }: { className?: string; }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 9v4m0 4h.01M10.3 4.3 2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SpinnerIcon({ className = '' }: { className?: string; }) {
  return (
    <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}
